import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  Overlay,
  OverlayTrigger,
  Popover,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { getStudents } from "../../../../../../store/onboarding/actions";
import Select from "react-select";
import {
  Student,
  StudentClass,
  StudentInfo,
} from "../../../../../../store/onboarding/types";
import { getFullName } from "../../../../../../utils/NamesUtils";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../store";
import { ValueType } from "react-select/src/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChalkboardTeacher } from "@fortawesome/free-solid-svg-icons";
import { UserInfo } from "../../../../../../store/auth/types";
import { GroupRecommendation } from "../../../../../../store/onboarding/cases/types";
import { changeRecommendedGroup } from "../../../../../../store/onboarding/cases/actions";
import { toastr } from "react-redux-toastr";
import DropdownOptionMenuList from "../../../../onboarding/second-step/evidence-tab/student-data-table/DropdownOptionMenuList";

type OwnProps = {
  target: any;
  selectedStudents: Student[];
  onAdd: (student: Student[], teacher?: UserInfo) => void;
  btnText: string;
  title: string;
  show: boolean;
  setShow: (flag: boolean) => any;
  placement?: any;
  loading?: boolean;
  withTeacher?: boolean;
  group?: GroupRecommendation;
  fromRecommendation?: boolean;
};

type Props = OwnProps;

const AddStudentOverlay: FunctionComponent<Props> = (props) => {
  const {
    show,
    setShow,
    target,
    selectedStudents,
    onAdd,
    btnText,
    title,
    placement,
    loading,
    withTeacher,
    group,
    fromRecommendation
  } = props;
  const dispatch = useDispatch();

  const [selectedRecommendedStudent, setSelectedRecommendedStudent] = useState<
    StudentInfo[]
  >([]);
  const [selectedStudent, setSelectedStudent] = useState<
    StudentInfo[]
  >([]);

  const students = useSelector(
    (s: ApplicationState) => s.onboarding.studentsRoster
  );

  const isLoading = useSelector(
    (s: ApplicationState) => s.onboarding.isLoading.getStudents
  );

  const studentOptions = useMemo(() => {
    return students.filter(
      (s) => !selectedStudents.some((ss) => ss.id === s.id)
    );
  }, [students, selectedStudents]);

  const handleSelectStudent = (value: ValueType<StudentInfo, true>) => {
    setSelectedStudent(value as StudentInfo[]);
    setSelectedTeacher(undefined);
  };

  const handleSelectRecommendedStudent = (value: ValueType<StudentInfo, any>) => {
    setSelectedRecommendedStudent((value as StudentInfo[]) ?? []);
    setSelectedTeacher(undefined);
  };

  const [selectedTeacher, setSelectedTeacher] = useState<any>(undefined);

  const handleSelectTeacher = (value: ValueType<UserInfo, false>) => {
    setSelectedTeacher(value as UserInfo);
  };

  const handleAddStudent = () => {
    if(selectedStudent.length === 0 && selectedRecommendedStudent.length === 0) {
      toastr.error("Alert", "Please select a student before continuing.");
      return;
    }
    if (selectedStudent.length) {
      onAdd(selectedStudent, selectedTeacher);
      setSelectedStudent([]);
    }
    if(selectedRecommendedStudent && !withTeacher) {
      dispatch(
        changeRecommendedGroup(group!, {
          ...group!,
          students: [...selectedRecommendedStudent, ...group!.students],
        })
      );
      setShow(false);
      setSelectedRecommendedStudent([]);
      toastr.success("Success", `Student added successfully! The student has been added to this group and can be found in the ${selectedStudent ? "‘Students Without Goal’" : "‘Students’"} section.`);
    }
  };

  const formatOptionLabel = (student: StudentInfo) => {
    return (
      <div className="d-flex align-items-center justify-content-between">
        <div>{getFullName(student).trim()}- {student.unique_id}</div>
        {(!!student.classes?.length || !!student.teachers?.length) && (
          <OverlayTrigger
            container={document.body}
            placement="top"
            overlay={
              <Tooltip id="fullName" className="customInfoTooltip text-left">
                <>
                  <strong>Teachers: </strong>
                  {student.teachers
                    ?.map((teacher) => getFullName(teacher))
                    .join(", ") || <i>None</i>}
                  <br />
                </>
                {/* <>
                  <strong>Classes: </strong>
                  {student.classes?.map((c: any) => c.name).join(", ") || (
                    <i>None</i>
                  )}
                </> */}
              </Tooltip>
            }
          >
            <FontAwesomeIcon className="ml-1" icon={faChalkboardTeacher} />
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const filterOption = (option: any, query: string) => {
    const input = query.toLowerCase();
    if (!input) return true;
    const label = option.label?.toLowerCase() ?? "";
    const id = option.data?.unique_id?.toString().toLowerCase() ?? "";
    const teachers = option.data.teachers?.some((t: any) =>
      getFullName(t).toLowerCase().includes(input)
    );
    const classes = !!option.data.classes?.some((c: StudentClass) =>
      c.name.toLowerCase().includes(input)
    );

    return label.includes(input) || id.includes(input) || teachers || classes;
  };

  const [searchText, setSearchText] = useState("");
  const rankStudents = (students: StudentInfo[], input: string) => {
    if (!input) return students;

    const query = input.toLowerCase().trim();
    const isNumeric = /^\d+$/.test(query);

    return [...students].sort((a, b) => {
      // Use unique_id for ranking if present, else fallback to id
      const aId = (a.unique_id ?? a.id)?.toString().toLowerCase() ?? "";
      const bId = (b.unique_id ?? b.id)?.toString().toLowerCase() ?? "";
      const aName = getFullName(a).toLowerCase();
      const bName = getFullName(b).toLowerCase();

      if (isNumeric) {
        // 1️⃣ Exact match — ABSOLUTE priority
        const aExact = aId === query;
        const bExact = bId === query;

        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // 2️⃣ Starts with
        const aStarts = aId.startsWith(query);
        const bStarts = bId.startsWith(query);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        // 3️⃣ Shorter ID wins (348 before 3487)
        if (aStarts && bStarts && aId.length !== bId.length) {
          return aId.length - bId.length;
        }

        // 4️⃣ Contains position
        const aIndex = aId.indexOf(query);
        const bIndex = bId.indexOf(query);

        if (aIndex !== bIndex) return aIndex - bIndex;

        // 5️⃣ Stable fallback
        return aId.localeCompare(bId);
      } else {
        // -------- Name ranking --------
        const aStarts = aName.startsWith(query);
        const bStarts = bName.startsWith(query);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        const aIndex = aName.indexOf(query);
        const bIndex = bName.indexOf(query);

        if (aIndex !== bIndex) return aIndex - bIndex;

        return aName.localeCompare(bName);
      }
    });
  };

  const rankedOptions = useMemo(
    () => rankStudents(studentOptions, searchText),
    [studentOptions, searchText]
  );

  return (
    <Overlay
      target={target.current}
      show={show}
      placement={placement}
      rootClose
      rootCloseEvent={"click"}
      onEnter={() => {
        if (!(students.length || isLoading)) {
          dispatch(getStudents(true));
        }
      }}
      onHide={() => {
        setShow(false);
        setSelectedStudent([]);
        fromRecommendation ? setSelectedRecommendedStudent([]) : '';
        setSelectedTeacher(undefined);
      }}
    >
      <Popover id="selectStudentsPopover" style={{ minWidth: "320px" }}>
        <Popover.Title>{title}</Popover.Title>
        <Popover.Content>
          {fromRecommendation ? 
            <Select
              isMulti
              components={{ MenuList: DropdownOptionMenuList }}
              value={selectedRecommendedStudent}
              onChange={handleSelectRecommendedStudent}
              placeholder="Search Students..."
              getOptionLabel={(student: any) => getFullName(student)}
              getOptionValue={(student: any) => student.id.toString()}
              options={rankedOptions}
              formatOptionLabel={formatOptionLabel}
              filterOption={filterOption}
              isLoading={isLoading}
              onInputChange={(value, meta) => {
                if (meta.action === "input-change") {
                  setSearchText(value);
                }
              }}
              onMenuClose={() => setSearchText("")}
            />
            : 
            <Select
              isMulti
              components={{ MenuList: DropdownOptionMenuList }}
              value={selectedStudent}
              onChange={handleSelectStudent}
              placeholder="Search Students..."
              getOptionLabel={(student: StudentInfo) => getFullName(student)}
              getOptionValue={(student: StudentInfo) => student.id!.toString()}
              options={rankedOptions}
              formatOptionLabel={formatOptionLabel}
              filterOption={filterOption}
              isLoading={isLoading}
              onInputChange={(value, meta) => {
                if (meta.action === "input-change") {
                  setSearchText(value);
                }
              }}
              onMenuClose={() => setSearchText("")}
            />
          }

          {selectedStudent && withTeacher && (
            <div className="mt-2">
              {selectedStudent?.filter((student) => student.teachers).length ? (
                <Select
                  value={selectedTeacher}
                  onChange={handleSelectTeacher}
                  placeholder="Select Teacher..."
                  getOptionLabel={(teacher: UserInfo) => getFullName(teacher)}
                  getOptionValue={(teacher: UserInfo) => teacher.id!.toString()}
                  options={selectedStudent?.flatMap((student) => student.teachers) || []}
                />
              ) : (
                <i>This student has no assigned teachers</i>
              )}
            </div>
          )}

            <button
            className="mt-2 blueBtnSm w-100"
            disabled={
              !(
              (selectedRecommendedStudent.length > 0 || selectedStudent !== undefined) &&
              (!(withTeacher) || selectedTeacher)
              )
            }
            onClick={handleAddStudent}
            >
            {btnText} {loading && <Spinner animation="border" size="sm" />}
            </button>
        </Popover.Content>
      </Popover>
    </Overlay>
  );
};

export default AddStudentOverlay;
